import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from '../../axiosBaseQuery';
import lubricantGroupsApiParser from './lubricantGroupsApiParser';

// ---------- Types ----------
export interface LubricantGroup {
  id: string | number;
  groupName: string;
  remarks: string;
}

export interface LubricantGroupsListResponse {
  data: LubricantGroup[];
}

// ---------- API Slice ----------
const lubricantGroupsApi = createApi({
  reducerPath: 'lubricantGroupsApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['LubricantGroup'],
  endpoints: (builder) => ({
    getLubricantGroupsList: builder.query<LubricantGroupsListResponse, any>({
      query: (params) => ({
        url: '/lubricant/group',
        method: 'GET',
        params: {
          ...params,
        },
      }),
      transformResponse: (res: any): LubricantGroupsListResponse => ({
        data: lubricantGroupsApiParser.LubricantGroupsListParser(res),
      }),
    }),

    createLubricantGroup: builder.mutation<any, { name: string; remarks: string }>({
      query: (payload) => ({
        url: '/lubricant/group',
        method: 'POST',
        data: payload,
      }),
      invalidatesTags: ['LubricantGroup'],
    }),

    updateLubricantGroup: builder.mutation<
      any,
      { id: number | string; name: string; remarks: string }
    >({
      query: ({ id, name, remarks }) => ({
        url: `/lubricant/group/${encodeURIComponent(id)}`,
        method: 'PUT',
        data: { name, remarks },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'LubricantGroup' as const, id: id.toString() },
        'LubricantGroup',
      ],
    }),

    deleteLubricantGroup: builder.mutation<any, number>({
      query: (id) => ({
        url: `/lubricant/group/${encodeURIComponent(id)}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['LubricantGroup'],
    }),
  }),
});

// ---------- Exports ----------
export const lubricantGroupsReducer = lubricantGroupsApi.reducer;
export const lubricantGroupsMiddleware = lubricantGroupsApi.middleware;
export const lubricantGroupsReducerPath = lubricantGroupsApi.reducerPath;

export const {
  useGetLubricantGroupsListQuery,
  useCreateLubricantGroupMutation,
  useUpdateLubricantGroupMutation,
  useDeleteLubricantGroupMutation,
} = lubricantGroupsApi;
